import { memo, useEffect, useState } from "react";
import { Leaf } from "lucide-react";
import type { GoldenSerenityComponentProps } from "./types";
import { GS, calculateCountdown, usePrefersReducedMotion } from "./utils";
import { AmbientGlow } from "./decorations";
import { countdownUnitLabelKey } from "../shared/countdown";

export const GoldenSerenityCountdown = memo(({ event, config, t }: GoldenSerenityComponentProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [timeLeft, setTimeLeft] = useState(() => calculateCountdown(event.date));

  useEffect(() => {
    // Reduced motion: no per-second ticking — refresh once a minute instead.
    const interval = prefersReducedMotion ? 60000 : 1000;
    const timer = setInterval(() => {
      setTimeLeft(calculateCountdown(event.date));
    }, interval);
    return () => clearInterval(timer);
  }, [event.date, prefersReducedMotion]);

  if (!event.date) return null;

  // JUB-94: singular/plural label per unit ("1 zi" vs "N zile"), resolved
  // the same way the golden_serenity template-engine package resolves it
  // (context.ts, buildCountdownParts) so the two never disagree.
  const items = [
    { label: t(countdownUnitLabelKey("days", timeLeft.days)), value: timeLeft.days, icon: "🌿" },
    { label: t(countdownUnitLabelKey("hours", timeLeft.hours)), value: timeLeft.hours, icon: "☀️" },
    { label: t(countdownUnitLabelKey("minutes", timeLeft.minutes)), value: timeLeft.minutes, icon: "🌸" },
    { label: t(countdownUnitLabelKey("seconds", timeLeft.seconds)), value: timeLeft.seconds, icon: "✨" },
  ];

  return (
    <section
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamDeep}, ${GS.creamLight}, ${GS.cream})` }}
    >
      <AmbientGlow />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 text-center">
        {/* Header with botanical accents */}
        <div className="mb-10 sm:mb-12 inline-flex items-center justify-center gap-3 max-w-full">
          <Leaf className="w-5 h-5 opacity-60 shrink-0" style={{ color: GS.sage }} aria-hidden="true" />
          <h2
            className="text-3xl sm:text-5xl font-medium text-balance"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.countdownBegan")}
          </h2>
          <Leaf className="w-5 h-5 opacity-60 rotate-180 shrink-0" style={{ color: GS.sage }} aria-hidden="true" />
        </div>

        {/* Countdown card */}
        <div className="relative max-w-4xl mx-auto">
          <div
            className="relative p-6 sm:p-10 rounded-2xl border"
            style={{
              background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.cream}, ${GS.creamDeep})`,
              borderColor: "rgba(232,220,200,0.4)",
              boxShadow: "0 8px 40px rgba(139,115,85,0.12)",
            }}
          >
            <div className="relative flex items-start justify-center gap-3 sm:gap-8 md:gap-12">
              {items.map((item) => (
                <div key={item.label} className="relative flex flex-col items-center group flex-1 sm:flex-none min-w-0">
                  <div className="mb-2 text-lg opacity-60" aria-hidden="true">
                    {item.icon}
                  </div>
                  <p
                    className="text-3xl sm:text-5xl md:text-6xl font-semibold leading-none tabular-nums"
                    style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                  >
                    {String(item.value).padStart(2, "0")}
                  </p>
                  <div
                    aria-hidden="true"
                    className="w-10 h-px my-2 opacity-30"
                    style={{ background: `linear-gradient(to right, transparent, ${GS.gold}, transparent)` }}
                  />
                  <p
                    className="text-[10px] sm:text-sm tracking-wider uppercase"
                    style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                  >
                    {item.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Bottom accent text */}
            <div className="mt-8 pt-6 border-t" style={{ borderColor: "rgba(232,220,200,0.4)" }}>
              <p
                className="text-xs tracking-wide text-center"
                style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
              >
                {t("events.form.template.untilLivesBecomeOne")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

GoldenSerenityCountdown.displayName = "GoldenSerenityCountdown";
